<template>
  <modal
    width="560px"
    bodyPadding="0"
    :title="isEdit ? t('Edit API Key') : t('Create API Key')"
    @close="close"
    readArticleUrl="https://api.bemob.com/docs/"
  >
    <div class="wrapper">
      <template v-if="step === 1">
        <div class="mb-16">
          <div
            v-html="
              t(
                'Select the access level for the API key.<br><b>Full Access</b> level of the API key provides unrestricted access to both data and functionality, allowing full control over resources.<br><b>Read Only</b> access allows you to fetch data or perform operations that retrieve information without making any modifications.<br>The API key will be created on the step 2 after the access level is configured.<br>The access level of a created key can’t be changed later.',
              )
            "
          />
          <div
            class="mt-16"
            v-html="
              t(
                '<b>Allow Access to URLs</b> allows this API key to retrieve URLs configured in offers, landing pages, campaigns and other links of your account.',
              )
            "
          />
        </div>
        <ui-radio-group
          v-model="formStep1.accessLevel"
          :label="t('Access Level')"
          :disabled="isEdit"
        >
          <ui-radio value="readOnly" :label="t('Read Only')" />
          <ui-radio value="fullAccess" :label="t('Full Access')" />
        </ui-radio-group>

        <ui-checkbox
          class="mt-16"
          v-model="formStep1.allowUrlAccess"
          :label="t('Allow Access to URLs')"
          :help-tooltip="t('Allows this API key to retrieve URLs configured in offers, landing pages, campaigns and other links')"
        />

        <workspace-select
          class="mt-16"
          :label="t('Workspace Selection')"
          :placeholder="t('All Workspaces')"
          :form="formStep1"
          :label-help-text="
            t(
              `by default, api keys are granted access to all workspaces in your account.<br>if you do not need separate api keys for different workspaces, leave <b>all workspaces</b> selected. otherwise, choose a specific workspace to limit the key's access.`,
            )
          "
          multiple
          chips
          multi-line
          :disabled="isEdit"
          :workspacesList="mainStore.allPrivateWorkspaces"
        />
      </template>
      <template v-if="step === 2">
        <div class="mb-16">
          {{
            t(
              'Please make sure to save the secret key in a secure location. Once the API key is created and the window is closed, you will no longer be able to access it.',
            )
          }}
        </div>

        <div>
          <ui-copy-input :label="t('Access Key')" :value="form.accessKey" />
        </div>

        <div class="mt-16">
          <ui-copy-input :label="t('Secret Key')" :value="form.secretKey" />
        </div>
      </template>
    </div>

    <template #footer>
      <section class="footer">
        <ui-button
          class="p-0"
          v-if="step === 1"
          :text="isEdit ? t('Save') : t('Next')"
          :icon-append="isEdit ? '' : 'fas fa-arrow-right'"
          :icon="isEdit ? 'fas fa-check' : ''"
          button-style="accent"
          :loading="loading"
          @click="submit"
        />
        <ui-button
          class="p-0"
          v-if="step === 2"
          :text="t('Done')"
          icon="fas fa-check"
          button-style="accent"
          @click="close"
        />
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { pick } from 'lodash';
import { useI18n } from '@/composables';
import { useMainStore } from '@/store';
import {
  postCreateUserApiKeyApiRequest,
  patchUserApiKeyApiRequest,
} from '@/services/api/user-api';
import WorkspaceSelect from '@/views/reports/pickers/wokrspace-select.vue';

type ApiKeyRecord = {
  accessKey?: string;
  accessLevel?: 'readOnly' | 'fullAccess';
  workspaceIds?: string[];
  allowUrlAccess?: boolean;
};

const props = defineProps<{
  data: {
    reloadApiKeys: () => Promise<void>;
    apiKey?: ApiKeyRecord;
  };
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const mainStore = useMainStore();

const isEdit = !!props.data?.apiKey;

const step = ref(1);
const loading = ref(false);
const formStep1 = ref({
  accessLevel: (props.data.apiKey?.accessLevel || 'readOnly') as 'readOnly' | 'fullAccess',
  workspaceId: (props.data.apiKey?.workspaceIds || []) as string[],
  allowUrlAccess: Boolean(props.data.apiKey?.allowUrlAccess ?? true),
});
const form = ref<{ accessKey?: string; secretKey?: string }>({});

const close = () => {
  emit('close');
};

const submit = async () => {
  if (isEdit) {
    const accessKey = props.data.apiKey?.accessKey;
    if (formStep1.value.allowUrlAccess || !accessKey) {
      close();
      return;
    }
    loading.value = true;
    try {
      await patchUserApiKeyApiRequest(accessKey, {
        allowUrlAccess: false,
      });
      await props.data.reloadApiKeys();
      close();
    } finally {
      loading.value = false;
    }
    return;
  }

  loading.value = true;
  try {
    const newKey = (await postCreateUserApiKeyApiRequest({
      accessLevel: formStep1.value.accessLevel,
      workspaceIds: formStep1.value.workspaceId,
      allowUrlAccess: formStep1.value.allowUrlAccess,
    })) as Record<string, unknown>;
    await props.data.reloadApiKeys();
    form.value = {
      ...pick(newKey, ['accessKey', 'secretKey']),
    } as { accessKey?: string; secretKey?: string };
    step.value = 2;
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped>

.footer {
  display: flex;
  justify-content: flex-end;
}

.wrapper {
  padding: 24px;
}
</style>
